import { useQuery } from '@tanstack/react-query'
import type { ActivityCatalogEntry, HistorySnapshot } from '../types'
import * as api from '../api/client'
import { DecisionRow } from './DecisionHistoryList'

interface Props {
  sessionId: string
  teamId: string
  catalog: ActivityCatalogEntry[]
}

export default function GameRecap({ sessionId, teamId, catalog }: Props) {
  const historyQ = useQuery({
    queryKey: ['session-history', sessionId],
    queryFn: () => api.getSessionHistory(sessionId),
  })
  const snapshots = historyQ.data?.teams[teamId]?.snapshots ?? []
  const final = snapshots[snapshots.length - 1] ?? null
  const byId = new Map(catalog.map((a) => [a.id, a]))

  return (
    <div className="sticky top-32 max-h-[calc(100vh-9rem)] flex flex-col gap-4">
      <div className="card shrink-0">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wide text-ok font-medium">
            Game Complete
          </div>
          <h2 className="text-base font-semibold mt-0.5">Final Recap</h2>
        </div>
        {final ? (
          <FinalScorecard scorecard={final.scorecard} />
        ) : (
          <p className="text-sm text-slate-500 italic">No snapshots yet.</p>
        )}
      </div>

      {snapshots.length > 0 && (
        <div className="card flex-1 min-h-0 flex flex-col">
          <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-3 shrink-0">
            Decisions
          </h3>
          <ol className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-2">
            {snapshots.map((snap: HistorySnapshot) => (
              <DecisionRow
                key={snap.quarter}
                snap={snap}
                byId={byId}
                boardOutcome={snap.board_request_outcome}
              />
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

function FinalScorecard({
  scorecard,
}: {
  scorecard: HistorySnapshot['scorecard']
}) {
  const composite = scorecard.composite
  const compositePct = Math.max(0, Math.min(100, composite))
  return (
    <div>
      <div className="stat-label">Final Score</div>
      <div className="flex items-baseline gap-2 mt-0.5 mb-2">
        <span className="text-3xl font-semibold tabular-nums text-slate-900">
          {composite.toFixed(2)}
        </span>
        <span className="text-sm text-slate-500 tabular-nums">/ 100</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full bg-brand-600"
          style={{ width: `${compositePct}%` }}
        />
      </div>
    </div>
  )
}
